import get from 'lodash/get';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import set from 'lodash/set';
import { type SliceCaseReducers } from '@direct-frontend/modules-store';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import {
    type GlobalSettings,
    type OpenApiSchemaPageMeta,
    type OpenApiSchemaSectionTemplate,
    type OpenApiSchemaSectionTemplateFragment,
    type OpenApiSchemaSectionTemplateFragmentsDiff,
    type Page,
    type PreviewPage,
    type PreviewSection,
    type Section,
    type SharedSection,
} from '#shared/api/schema';
import { type YandexKit } from '#shared/components/YandexKitProvider';
import { showError } from '#shared/lib/toast';
import { getConstructorTemplateSections, getLatestVersion, getVersion } from '../../api';
import {
    addConstructorTemplateSection,
    deleteConstructorTemplateSection,
    updateConstructorTemplateSection,
} from '../../api/constructor/api';
import { buildConstructorPartialDtosFromFragments } from '../../lib/buildPartials';
import { constructorProductLoggingEvents } from '../../lib/constructorProductLoggingEvents';
import { store } from '../../store';
import { CONSTRUCTOR_FOOTER_ID, CONSTRUCTOR_HEADER_ID, CONSTRUCTOR_MYSTIQUE_ID } from '../constants';
import { settingsKeyMap } from '../utils/constants';
import { getConstructorPageOrder } from '../utils/getConstructorPageOrder';
import { mapKeysDeep } from '../utils/mapKeysDeep';
import { type ConstructorRightBarSectionType, type FnReducer } from './types';

type ConstructorPreviewPageLayoutItem = {
    instance_id: string;
    id: string;
    display_sequence: number;
    section_type: Section['section_type'];
    status?: Section['status'];
};

type ConstructorPreviewPage = Omit<Page, 'layout'> & {
    layout: ConstructorPreviewPageLayoutItem[];
};

interface ConstructorPreviewState {
    isDirty: boolean;
    isFirstTime: boolean;
    hasAppliedTemplateDraft: boolean;
    templateDraftGeneration: number;
    isError: boolean;
    isLocked: boolean;
    activeVersion: number;
    page: string;
    pages: {
        [pageId: string]: ConstructorPreviewPage;
    };
    sections: {
        [sectionId: string]: SharedSection;
    };
    templates: {
        [templateId: string]: OpenApiSchemaSectionTemplate;
    };
    fragments: {
        [fragmentId: string]: OpenApiSchemaSectionTemplateFragment;
    };
    globalSettings: GlobalSettings;
    currentSection?: ConstructorRightBarSectionType;
}

const initialStateConstructorPreview: ConstructorPreviewState = {
    isDirty: false,
    isFirstTime: false,
    hasAppliedTemplateDraft: false,
    templateDraftGeneration: 0,
    isError: false,
    isLocked: false,
    activeVersion: 0,
    page: '',
    pages: {},
    sections: {},
    templates: {},
    fragments: {},
    globalSettings: {},
};

const reducerChangeActiveVersion: FnReducer<ConstructorPreviewState, number> = (state, action) => {
    state.activeVersion = action.payload;
};

const reducerChangeIsFirstTime: FnReducer<ConstructorPreviewState, boolean> = (state, action) => {
    state.isFirstTime = action.payload;
};

const reducerBeginAppliedTemplateDraft: FnReducer<ConstructorPreviewState> = (state) => {
    state.hasAppliedTemplateDraft = true;
    state.isDirty = true;
    state.templateDraftGeneration += 1;
};

const reducerFinishAppliedTemplateDraft: FnReducer<ConstructorPreviewState> = (state) => {
    state.hasAppliedTemplateDraft = false;
    state.templateDraftGeneration += 1;
};

const reducerChangeIsError: FnReducer<ConstructorPreviewState, boolean> = (state, action) => {
    state.isError = action.payload;
};

const reducerChangeCurrentSection: FnReducer<ConstructorPreviewState, ConstructorRightBarSectionType> = (
    state,
    action
) => {
    state.currentSection = action.payload;
};

const reducerChangePage: FnReducer<ConstructorPreviewState, string> = (state, action) => {
    const pageId = Object.values(state.pages).find(
        (page) => page.alias === action.payload || page.id === action.payload
    )?.id;

    state.page = pageId ?? action.payload;
};

const reducerChangePreviewIsLocked: FnReducer<ConstructorPreviewState, boolean> = (state, action) => {
    state.isLocked = action.payload;
};

const reducerChangePageSettings: FnReducer<
    ConstructorPreviewState,
    { pageId: string; pageSettings: Page; withoutLayout?: boolean }
> = (state, action) => {
    const { pageId, pageSettings, withoutLayout } = action.payload;

    state.pages[pageId] = {
        ...pageSettings,
        layout: pageSettings.layout.map((section) => {
            return {
                id: section.id,
                display_sequence: section.display_sequence,
                instance_id: section.instance_id,
                section_type: section.section_type,
                status: section.status,
            };
        }),
    };

    state.isDirty = true;

    if (withoutLayout) {
        return;
    }

    pageSettings.layout.forEach((section) => {
        reducerChangeSectionSettings(state, {
            payload: {
                pageId: pageSettings.id,
                sectionInstanceId: section.instance_id,
                moduleId: undefined,
                sectionSettings: section,
            },
            type: 'constructor/reducerChangePageSettings',
        });
    });
};

const reducerChangeTemplate: FnReducer<
    ConstructorPreviewState,
    { templateId: string; template?: OpenApiSchemaSectionTemplate }
> = (state, action) => {
    const { templateId, template } = action.payload;

    // Создаём/Обновляем шаблон
    if (template) {
        state.templates[templateId] = template;
    } else {
        delete state.templates[templateId];
    }
};

const reducerChangeFragment: FnReducer<
    ConstructorPreviewState,
    { fragmentId: string; fragment?: OpenApiSchemaSectionTemplateFragment }
> = (state, action) => {
    const { fragmentId, fragment } = action.payload;

    if (fragment) {
        state.fragments[fragmentId] = fragment;
    } else {
        delete state.fragments[fragmentId];
    }
};

const reducerSetFragments: FnReducer<ConstructorPreviewState, OpenApiSchemaSectionTemplateFragment[]> = (
    state,
    action
) => {
    state.fragments = action.payload.reduce<ConstructorPreviewState['fragments']>((acc, fragment) => {
        acc[fragment.id] = fragment;

        return acc;
    }, {});
};

const reducerChangeSectionSettings: FnReducer<
    ConstructorPreviewState,
    { pageId: string; sectionInstanceId: string; moduleId?: string; sectionSettings?: Section }
> = (state, action) => {
    const { pageId, sectionInstanceId, moduleId, sectionSettings } = action.payload;

    const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

    state.isDirty = true;

    // Обновляем модули только если секция существует на странице
    if (typeof sectionIndex === 'number' && sectionIndex !== -1 && moduleId) {
        const sectionId = state.pages[pageId].layout[sectionIndex].id;
        const allModules = get(state.sections[sectionId], `settings.modules`, []);

        if (Array.isArray(allModules)) {
            const index = allModules.findIndex((item) => item.id === moduleId);
            const found = index !== -1;

            if (!found && sectionSettings) {
                // Добавляем модуль
                allModules.push(sectionSettings);
            } else if (found && sectionSettings) {
                // Обновляем модуль
                allModules[index] = sectionSettings;
            } else if (found && !sectionSettings) {
                // Удаляем модуль
                allModules.splice(index, 1);
            }

            allModules.sort((a, b) => a.display_sequence - b.display_sequence);

            set(state.sections[sectionId].settings, 'modules', allModules);
        } else if (state.sections[sectionId]) {
            set(state.sections[sectionId], `settings.modules[${moduleId}]`, sectionSettings);
        }
    } else if (!sectionSettings && sectionIndex !== -1) {
        // Удаляем секцию
        state.pages[pageId].layout.splice(sectionIndex, 1);
    } else if (sectionSettings) {
        // Создаём / Обновляем секцию
        const { display_sequence, instance_id, section_type, ...sharedSettings } = sectionSettings;

        const sectionId =
            sectionIndex === -1
                ? (sharedSettings.id ?? self.crypto.randomUUID())
                : (sharedSettings.id ?? state.pages[pageId].layout[sectionIndex].id);

        const existingSection =
            sectionIndex === -1 ? undefined : state.sections[state.pages[pageId].layout[sectionIndex].id];
        const existingStatus = sectionIndex === -1 ? undefined : state.pages[pageId].layout[sectionIndex].status;
        const isReplacingCustomSection =
            existingSection?.widget === CONSTRUCTOR_MYSTIQUE_ID &&
            Boolean(sharedSettings.widget) &&
            sharedSettings.widget !== CONSTRUCTOR_MYSTIQUE_ID;
        const layoutStatus =
            sectionSettings.status ?? (isReplacingCustomSection ? 'published' : existingStatus) ?? 'published';

        if (sectionIndex === -1) {
            state.pages[pageId].layout.push({
                id: sectionId,
                display_sequence,
                instance_id,
                section_type,
                status: layoutStatus,
            });
        } else {
            state.pages[pageId].layout[sectionIndex] = {
                id: sectionId,
                display_sequence,
                instance_id,
                section_type,
                status: layoutStatus,
            };
        }

        state.sections[sectionId] = {
            ...sharedSettings,
            id: sectionId,
            settings: {
                ...merge({}, state.sections[sectionSettings.id]?.settings),
                ...sharedSettings.settings,
            },
        };
    }
};

const resolveAddSectionSectionId = (
    state: ConstructorPreviewState,
    sectionSettings: Omit<Section, 'instance_id'>,
    isUnique?: boolean
): string => {
    if (isUnique) {
        const existingSettings = Object.values(state.pages)
            .reduce(
                (sections: ConstructorPreviewPage['layout'], page: ConstructorPreviewPage) => [
                    ...sections,
                    ...page.layout,
                ],
                []
            )
            .map((section) => {
                return { ...section, ...state.sections[section.id] };
            })
            .find((section) => section.widget === sectionSettings.widget && section.section_type === 'parent');
        const defaultSettings = {
            id: sectionSettings.id,
            widget: sectionSettings.widget,
            settings: sectionSettings.settings ?? {},
        } as Section;
        const settings = existingSettings ? existingSettings : defaultSettings;

        return settings.id ?? self.crypto.randomUUID();
    }

    return sectionSettings.id ?? self.crypto.randomUUID();
};

const reducerAddSection: FnReducer<
    ConstructorPreviewState,
    { pageId: string; sectionInstanceId: string; sectionSettings: Omit<Section, 'instance_id'>; isUnique?: boolean }
> = (state, action) => {
    const { pageId, sectionInstanceId: newInstanceId, sectionSettings, isUnique } = action.payload;

    if (isUnique) {
        const existingSettings = Object.values(state.pages)
            .reduce(
                (sections: ConstructorPreviewPage['layout'], page: ConstructorPreviewPage) => [
                    ...sections,
                    ...page.layout,
                ],
                []
            )
            .map((section) => {
                return { ...section, ...state.sections[section.id] };
            })
            .find((section) => section.widget === sectionSettings.widget && section.section_type === 'parent');
        const defaultSettings = {
            id: sectionSettings.id,
            widget: sectionSettings.widget,
            settings: sectionSettings.settings ?? {},
        } as Section;
        const settings = existingSettings ? existingSettings : defaultSettings;
        const sectionId = sectionSettings.id ?? settings.id ?? self.crypto.randomUUID();

        reducerChangeSectionSettings(state, {
            payload: {
                pageId: pageId,
                sectionInstanceId: newInstanceId,
                moduleId: undefined,
                sectionSettings: {
                    ...settings,
                    display_sequence: sectionSettings.display_sequence,
                    id: sectionId,
                    instance_id: newInstanceId,
                    section_type: existingSettings?.id ? 'child' : 'parent',
                },
            },
            type: 'constructor/reducerChangeSectionSettings',
        });
        state.currentSection = {
            sectionInstanceId: newInstanceId,
        };

        return;
    }

    let sectionType: Section['section_type'] = 'usual';

    if (sectionSettings?.section_type) {
        const hasLivingParent = Object.values(state.pages).some((page: ConstructorPreviewPage) =>
            page.layout.some((section) => section.id === sectionSettings.id && section.section_type === 'parent')
        );

        // Если такая секция уже есть, позволяет добавляем нужный тип, если нет - по необходимости вставляем parent
        if (hasLivingParent) {
            sectionType = sectionSettings.section_type;
        } else if (!hasLivingParent && sectionSettings.section_type !== 'usual') {
            sectionType = 'parent';
        }
    }

    const sectionId = sectionSettings.id ?? self.crypto.randomUUID();

    reducerChangeSectionSettings(state, {
        payload: {
            pageId: pageId,
            sectionInstanceId: newInstanceId,
            moduleId: undefined,
            sectionSettings: {
                ...sectionSettings,
                id: sectionId,
                section_type: sectionType,
                instance_id: newInstanceId,
            },
        },
        type: 'constructor/reducerChangeSectionSettings',
    });
    state.currentSection = {
        sectionInstanceId: newInstanceId,
    };
};

const reducerResetAll: FnReducer<ConstructorPreviewState> = (state) => {
    state.pages = {};
    state.sections = {};
    state.globalSettings = {};
    state.isFirstTime = false;
    state.hasAppliedTemplateDraft = false;
    state.templateDraftGeneration += 1;
    state.currentSection = undefined;
    state.isDirty = false;
};

const reducerChangeGlobalSettings: FnReducer<ConstructorPreviewState, GlobalSettings> = (state, action) => {
    state.globalSettings = action.payload;
    state.isDirty = true;
};

const reducerChangeIsDirty: FnReducer<ConstructorPreviewState, boolean> = (state, action) => {
    state.isDirty = action.payload;
};

const reducerAddPage: FnReducer<ConstructorPreviewState, Omit<Page, 'id'> & { layout?: Page['layout'] }> = (
    state,
    action
) => {
    const newId = self.crypto.randomUUID();

    if (action.payload.layout) {
        state.pages[newId] = {
            ...action.payload,
            id: newId,
            exact: true,
        };
        action.payload.layout.forEach((section) => {
            if (!state.sections[section.id]) {
                state.sections[section.id] = {
                    id: section.id,
                    widget: section.widget,
                    settings: section.settings,
                    status: section.status ?? 'published',
                };
            }
        });
        state.page = newId;
        state.isDirty = true;

        return;
    }

    const header = Object.values(state.sections).find((section) => section.widget === CONSTRUCTOR_HEADER_ID);
    const footer = Object.values(state.sections).find((section) => section.widget === CONSTRUCTOR_FOOTER_ID);
    let headerSequence = 1;
    let footerSequence = 9999;

    if (header && footer) {
        const headerIndex = state.pages[state.page].layout.findIndex((section) => section.id === header.id);
        const footerIndex = state.pages[state.page].layout.findIndex((section) => section.id === footer.id);

        if (headerIndex !== -1 && footerIndex !== -1) {
            headerSequence = state.pages[state.page].layout[headerIndex].display_sequence;
            footerSequence = state.pages[state.page].layout[footerIndex].display_sequence;
        }
    }

    const layout = [];

    if (header) {
        const hasParent = Object.values(state.pages).some((page) =>
            page.layout.some((section) => section.id === header.id && section.section_type === 'parent')
        );
        let headerId = header.id;

        if (!hasParent) {
            headerId = self.crypto.randomUUID();
            state.sections[headerId] = {
                id: headerId,
                widget: header.widget,
                settings: header.settings,
                status: header.status ?? 'published',
            };
        }

        layout.push({
            id: headerId,
            display_sequence: headerSequence,
            instance_id: self.crypto.randomUUID(),
            section_type: hasParent ? ('child' as const) : ('usual' as const),
        });
    }

    if (footer) {
        const hasParent = Object.values(state.pages).some((page) =>
            page.layout.some((section) => section.id === footer.id && section.section_type === 'parent')
        );
        let footerId = footer.id;

        if (!hasParent) {
            footerId = self.crypto.randomUUID();
            state.sections[footerId] = {
                id: footerId,
                widget: footer.widget,
                settings: footer.settings,
                status: footer.status ?? 'published',
            };
        }

        layout.push({
            id: footerId,
            display_sequence: footerSequence,
            instance_id: self.crypto.randomUUID(),
            section_type: hasParent ? ('child' as const) : ('usual' as const),
        });
    }

    state.pages[newId] = {
        id: newId,
        ...action.payload,
        layout,
        exact: true,
    };
    state.page = newId;
    state.isDirty = true;
};

const reducerDeletePage: FnReducer<ConstructorPreviewState, string> = (state, action) => {
    if (!state.pages[action.payload]) {
        return;
    }

    state.pages[action.payload].layout.forEach((section) => {
        if (section.section_type === 'parent') {
            reducerDetachSection(state, {
                payload: {
                    pageId: state.pages[action.payload].id,
                    sectionInstanceId: section.instance_id,
                },
                type: 'constructor/reducerDetachSection',
            });
        }
    });

    delete state.pages[action.payload];
    state.isDirty = true;

    if (state.page === action.payload) {
        state.page = Object.keys(state.pages)[0];
    }
};

const reducerChangePageContext: FnReducer<ConstructorPreviewState, Page['static_context']> = (state, action) => {
    if (!state.pages[state.page]) {
        return;
    }

    state.pages[state.page].static_context = action.payload;
};

const reducerDetachSection: FnReducer<
    ConstructorPreviewState,
    { pageId: string; sectionInstanceId: string; detachAll?: boolean }
> = (state, action) => {
    const { pageId, sectionInstanceId, detachAll } = action.payload;
    const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

    if (sectionIndex === undefined || sectionIndex === -1) {
        return;
    }

    const sectionSettings = state.pages[pageId].layout[sectionIndex];

    if (sectionSettings.section_type === 'usual') {
        return;
    }

    const uniqueSettings = state.sections[sectionSettings.id];

    if (sectionSettings.section_type === 'parent') {
        let choseNewParent = false;

        Object.values(state.pages).forEach((page) => {
            page.layout.forEach((section, index) => {
                if (section.id === sectionSettings.id) {
                    const targetedSection = section.instance_id === sectionSettings.instance_id;
                    const shouldBeParent = !detachAll && !choseNewParent && !targetedSection;

                    if (shouldBeParent) {
                        choseNewParent = true;
                    }

                    const newId = targetedSection ? self.crypto.randomUUID() : section.id;
                    let newSectionType = section.section_type;

                    if (shouldBeParent) {
                        newSectionType = 'parent';
                    } else if (targetedSection || detachAll) {
                        newSectionType = 'usual';
                    }

                    state.sections[newId] = {
                        ...uniqueSettings,
                        id: newId,
                    };
                    state.pages[page.id].layout[index] = {
                        ...section,
                        section_type: newSectionType,
                        id: newId,
                    };
                }
            });
        });
    } else {
        const newId = self.crypto.randomUUID();

        state.sections[newId] = {
            ...uniqueSettings,
            id: newId,
        };
        state.pages[pageId].layout[sectionIndex] = {
            ...sectionSettings,
            section_type: 'usual',
            id: newId,
        };
    }
};

const reducerDeleteSection: FnReducer<
    ConstructorPreviewState,
    { pageId: string; sectionInstanceId: string; deleteAll?: boolean }
> = (state, action) => {
    const { pageId, sectionInstanceId, deleteAll } = action.payload;
    const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

    if (sectionIndex === undefined || sectionIndex === -1) {
        return;
    }

    const sectionSettings = state.pages[pageId].layout[sectionIndex];

    reducerChangeSectionSettings(state, {
        payload: {
            pageId,
            sectionInstanceId,
            moduleId: undefined,
            sectionSettings: undefined,
        },
        type: 'constructor/reducerChangePageSettings',
    });

    if (sectionSettings.section_type === 'parent') {
        let choseNewParent = false;

        Object.values(state.pages).forEach((page) => {
            page.layout.forEach((section, index) => {
                if (section.id === sectionSettings.id) {
                    if (deleteAll) {
                        reducerChangeSectionSettings(state, {
                            payload: {
                                pageId: page.id,
                                sectionInstanceId: section.instance_id,
                                moduleId: undefined,
                                sectionSettings: undefined,
                            },
                            type: 'constructor/reducerChangePageSettings',
                        });
                    } else {
                        if (choseNewParent) {
                            return;
                        }

                        const shouldBeParent = !deleteAll && !choseNewParent;

                        if (shouldBeParent) {
                            choseNewParent = true;
                        }

                        state.pages[page.id].layout[index] = {
                            ...section,
                            section_type: 'parent',
                        };
                    }
                }
            });
        });
    }
};

const reducerDeleteTemplate: FnReducer<ConstructorPreviewState, { templateId: string }> = (state, action) => {
    const { templateId } = action.payload;

    const sections = Object.values(state.sections).filter((section) => section.template_id === templateId);

    if (!sections.length) {
        delete state.templates[templateId];

        return;
    }

    Object.values(state.pages).forEach((page) => {
        page.layout.forEach((section) => {
            if (sections.find((sectionToDelete) => sectionToDelete.id === section.id)) {
                reducerChangeSectionSettings(state, {
                    payload: {
                        pageId: page.id,
                        sectionInstanceId: section.instance_id,
                        moduleId: undefined,
                        sectionSettings: undefined,
                    },
                    type: 'constructor/reducerChangePageSettings',
                });
            }
        });
    });
    delete state.templates[templateId];
};

const reducerMakeParentSection: FnReducer<ConstructorPreviewState, { pageId: string; sectionInstanceId: string }> = (
    state,
    action
) => {
    const { pageId, sectionInstanceId } = action.payload;
    const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

    if (sectionIndex === undefined || sectionIndex === -1) {
        return;
    }

    const sectionSettings = state.pages[pageId].layout[sectionIndex];

    if (!sectionSettings || sectionSettings.section_type === 'parent') {
        return;
    }

    const parentPage = Object.values(state.pages).find((page) =>
        page.layout.some((section) => section.id === sectionSettings.id && section.section_type === 'parent')
    );

    const parentSection =
        parentPage?.id &&
        state.pages[parentPage?.id]?.layout.find(
            (section) => section.id === sectionSettings.id && section.section_type === 'parent'
        );
    const uniqueSettings = state.sections[sectionSettings.id];

    if (parentSection) {
        reducerChangeSectionSettings(state, {
            payload: {
                pageId: parentPage.id,
                sectionInstanceId: parentSection.instance_id,
                moduleId: undefined,
                sectionSettings: {
                    ...uniqueSettings,
                    ...parentSection,
                    section_type: 'child',
                },
            },
            type: 'constructor/reducerChangePageSettings',
        });
    }

    reducerChangeSectionSettings(state, {
        payload: {
            pageId: pageId,
            sectionInstanceId: sectionSettings.instance_id,
            moduleId: undefined,
            sectionSettings: {
                ...uniqueSettings,
                ...sectionSettings,
                section_type: 'parent',
            },
        },
        type: 'constructor/reducerChangePageSettings',
    });
};

const reducerChangeSectionStatus: FnReducer<
    ConstructorPreviewState,
    { pageId: string; sectionInstanceId: string; status: Section['status']; hideAll?: boolean }
> = (state, action) => {
    const { pageId, sectionInstanceId, status, hideAll } = action.payload;
    const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

    if (sectionIndex === undefined || sectionIndex === -1) {
        return;
    }

    const sectionSettings = state.pages[pageId].layout[sectionIndex];

    state.pages[pageId].layout[sectionIndex] = {
        ...sectionSettings,
        status,
    };
    state.isDirty = true;

    if (!hideAll || sectionSettings.section_type !== 'parent') {
        return;
    }

    Object.values(state.pages).forEach((page) => {
        page.layout.forEach((section, index) => {
            if (section.id === sectionSettings.id && section.section_type === 'child') {
                state.pages[page.id].layout[index] = {
                    ...section,
                    status,
                };
            }
        });
    });
};

const constructorSlice = store.createSlice<ConstructorPreviewState, SliceCaseReducers<ConstructorPreviewState>>({
    name: 'preview',
    initialState: initialStateConstructorPreview,
    reducers: {
        reducerChangeIsError,
        reducerChangeCurrentSection,
        reducerAddPage,
        reducerChangePage,
        reducerDeletePage,
        reducerAddSection,
        reducerChangeSectionSettings,
        reducerChangeSectionStatus,
        reducerChangePreviewIsLocked,
        reducerResetAll,
        reducerChangeGlobalSettings,
        reducerChangePageSettings,
        reducerChangePageContext,
        reducerChangeIsFirstTime,
        reducerBeginAppliedTemplateDraft,
        reducerFinishAppliedTemplateDraft,
        reducerChangeActiveVersion,
        reducerChangeIsDirty,
        reducerDetachSection,
        reducerDeleteSection,
        reducerMakeParentSection,
        reducerChangeTemplate,
        reducerDeleteTemplate,
        reducerChangeFragment,
        reducerSetFragments,
    },
});

export const changeActiveVersion = constructorSlice.reducerChangeActiveVersion;
export const changeIsFirstTime = constructorSlice.reducerChangeIsFirstTime;
export const beginAppliedTemplateDraft = constructorSlice.reducerBeginAppliedTemplateDraft;
export const finishAppliedTemplateDraft = constructorSlice.reducerFinishAppliedTemplateDraft;
export const changeConstructorIsDirty = constructorSlice.reducerChangeIsDirty;
export const resetConstructorAll = constructorSlice.reducerResetAll;

export const changeConstructorPageStatus = (pageId: string, pageStatus: Page['status']) => {
    logEventString(constructorProductLoggingEvents.Page.Visibility, pageStatus, { pageId });

    const pageSettings = getConstructorPageSettings(pageId);

    return constructorSlice.reducerChangePageSettings({
        pageId,
        pageSettings: { ...pageSettings, status: pageStatus },
        withoutLayout: true,
    });
};

export const changeConstructorRightBarSection = (sectionInstanceId?: string, moduleId?: string) =>
    constructorSlice.reducerChangeCurrentSection({ sectionInstanceId, moduleId });
export const changeConstructorPage = constructorSlice.reducerChangePage;
export const changeConstructorPreviewIsLocked = constructorSlice.reducerChangePreviewIsLocked;
export const setConstructorFragments = constructorSlice.reducerSetFragments;

export const addConstructorSection = (pageId: string, sectionSettings: Object, isUnique: boolean): string => {
    const settings = sectionSettings as Omit<Section, 'instance_id'>;
    const state = getConstructorPreviewState();
    const sectionId = resolveAddSectionSectionId(state, settings, isUnique);
    const sectionInstanceId = self.crypto.randomUUID();

    constructorSlice.reducerAddSection({
        pageId,
        sectionInstanceId,
        sectionSettings: { ...settings, id: sectionId },
        isUnique,
    });

    return sectionInstanceId;
};

export const changeConstructorSection = (pageId: string, sectionInstanceId: string, sectionSettings: Object) =>
    constructorSlice.reducerChangeSectionSettings({ pageId, sectionInstanceId, moduleId: undefined, sectionSettings });

export const changeConstructorSectionStatus = (
    pageId: string,
    sectionInstanceId: string,
    sectionStatus: Section['status'],
    hideAll?: boolean
) => {
    logEventString(constructorProductLoggingEvents.Section.Visibility, sectionStatus, {
        pageId,
        sectionInstanceId,
        hideAll: Boolean(hideAll),
    });

    return constructorSlice.reducerChangeSectionStatus({
        pageId,
        sectionInstanceId,
        status: sectionStatus,
        hideAll,
    });
};

export const deleteConstructorSection = (pageId: string, sectionInstanceId: string, deleteAll?: boolean) =>
    constructorSlice.reducerDeleteSection({
        pageId,
        sectionInstanceId,
        deleteAll,
    });

export const addConstructorModule = (
    pageId: string,
    sectionInstanceId: string,
    moduleId: string,
    sectionSettings: Object
) => constructorSlice.reducerChangeSectionSettings({ pageId, sectionInstanceId, moduleId, sectionSettings });

export const changeConstructorModule = (
    pageId: string,
    sectionInstanceId: string,
    moduleId: string,
    sectionSettings: Object
) => constructorSlice.reducerChangeSectionSettings({ pageId, sectionInstanceId, moduleId, sectionSettings });

export const deleteConstructorModule = (pageId: string, sectionInstanceId: string, moduleId: string) =>
    constructorSlice.reducerChangeSectionSettings({ pageId, sectionInstanceId, moduleId, sectionSettings: undefined });

export const changeConstructorGlobalSettings = constructorSlice.reducerChangeGlobalSettings;

export const addConstructorPage = (payload: Omit<Partial<Page>, 'id'> & { layout?: Page['layout'] }) => {
    logEventString(constructorProductLoggingEvents.Page.Add, 'true', {
        alias: String(payload.alias ?? ''),
        variantType: String(payload.variant_type ?? ''),
    });

    return constructorSlice.reducerAddPage(payload);
};

export const renameConstructorPage = ({ pageId, title }: { pageId: string; title: string }) => {
    const pageSettings = getConstructorPageSettings(pageId);

    if (!pageSettings || pageSettings.title === title) {
        return;
    }

    logEventString(constructorProductLoggingEvents.Page.Rename, 'true', { pageId });

    changePageSettings({ pageId, pageSettings: { ...pageSettings, title } });
};

export const changeConstructorPagePattern = ({ pageId, pattern }: { pageId: string; pattern: string }) => {
    const pageSettings = getConstructorPageSettings(pageId);

    changePageSettings({ pageId, pageSettings: { ...pageSettings, pattern } });
};

export const changeConstructorPageMeta = ({ pageId, meta }: { pageId: string; meta: OpenApiSchemaPageMeta }) => {
    const pageSettings = getConstructorPageSettings(pageId);

    changePageSettings({ pageId, pageSettings: { ...pageSettings, meta } });
};

export const changeConstructorPageContext = (pageContext: Page['static_context']) =>
    constructorSlice.reducerChangePageContext(pageContext);

export const changePageSettings = constructorSlice.reducerChangePageSettings;

export const detachConstructorSection = ({
    pageId,
    sectionInstanceId,
    detachAll,
}: {
    pageId: string;
    sectionInstanceId: string;
    detachAll?: boolean;
}) => {
    constructorSlice.reducerDetachSection({ pageId, sectionInstanceId, detachAll });
};

export const makeConstructorSectionParent = ({
    pageId,
    sectionInstanceId,
}: {
    pageId: string;
    sectionInstanceId: string;
}) => {
    logEventString(constructorProductLoggingEvents.SectionType.MakeParent, 'true', { pageId, sectionInstanceId });

    constructorSlice.reducerMakeParentSection({ pageId, sectionInstanceId });
};

export const updateConstructorTemplate = async (
    template: OpenApiSchemaSectionTemplate,
    options?: {
        fragmentsDiff?: OpenApiSchemaSectionTemplateFragmentsDiff;
        usedFragments?: string[];
    }
) => {
    let newTemplate;
    let versionId;
    const fragmentsDiff = options?.fragmentsDiff ?? { create: [], update: [], delete: [] };
    const usedFragments = options?.usedFragments ?? [];

    if (template.id === 'new') {
        newTemplate = await addConstructorTemplateSection({
            ...template,
            fragments_diff: fragmentsDiff,
            used_fragments: usedFragments,
        });
    } else {
        newTemplate = await updateConstructorTemplateSection({
            params: { id: template.id },
            payload: {
                title: template.title,
                alias: template.alias,
                html: template.html,
                css: template.css,
                script: template.script,
                json_schema: template.json_schema,
                fragments_diff: fragmentsDiff,
                used_fragments: usedFragments,
            },
        });
        versionId = newTemplate.version.version_id;
    }

    constructorSlice.reducerChangeTemplate({ templateId: newTemplate.id, template: newTemplate });

    if ('all_fragments' in newTemplate && newTemplate.all_fragments) {
        constructorSlice.reducerSetFragments(newTemplate.all_fragments);
    }

    return {
        ...newTemplate,
        versionId,
    };
};

export const deleteConstructorTemplate = async (templateId: string) => {
    constructorSlice.reducerDeleteTemplate({ templateId });
    await deleteConstructorTemplateSection({ params: { id: templateId } });
};

export const [useConstructorActiveVersion, getConstructorActiveVersion] = constructorSlice.createSelector(
    (state) => state.activeVersion
);
export const [useConstructorIsError] = constructorSlice.createSelector((state) => state.isError);
export const [useConstructorIsFirstTime] = constructorSlice.createSelector((state) => state.isFirstTime);
export const [, getConstructorPreviewState] = constructorSlice.createSelector((state) => state);
export const [useConstructorRightBarSection, getConstructorRightBarSection] = constructorSlice.createSelector(
    (state) => state.currentSection
);
export const [useConstructorPreviewPage, getConstructorPreviewPage] = constructorSlice.createSelector(
    (state) => state.page
);
export const [useConstructorPreviewGlobalSettings] = constructorSlice.createSelector((state) => state.globalSettings);
export const [useConstructorPageContext] = constructorSlice.createSelector(
    (state) => state.pages[state.page]?.static_context
);

export const [useConstructorIsDirty, getConstructorIsDirty] = constructorSlice.createSelector((state) => state.isDirty);

export const [useConstructorPreviewIsLocked] = constructorSlice.createSelector((state) => state.isLocked);

export const [useConstructorSectionSettings, getConstructorSectionSettings] = constructorSlice.createSelector(
    (state, pageId: string, sectionInstanceId?: string, moduleId?: string, withModules?: boolean) => {
        if (!sectionInstanceId) {
            return undefined;
        }

        const sectionIndex = state.pages[pageId]?.layout.findIndex((item) => item.instance_id === sectionInstanceId);

        if (typeof sectionIndex !== 'number' || sectionIndex === -1) {
            return undefined;
        }

        const pageSectionSettings = state.pages[pageId]?.layout[sectionIndex];

        if (!pageSectionSettings) {
            return undefined;
        }

        const allModules = get(state.sections[pageSectionSettings.id], `settings.modules`);

        if (!state.sections[pageSectionSettings.id] || (moduleId && !allModules)) {
            return undefined;
        }

        const sectionSettings = state.sections[pageSectionSettings.id];

        // Если требуется модуль, то возвращаем его
        if (moduleId) {
            const allModules = get(sectionSettings, 'settings.modules');

            if (Array.isArray(allModules)) {
                return allModules.find((item) => item.id === moduleId);
            }

            return get(sectionSettings, `settings.modules[${moduleId}]`);
        }

        // Всё, кроме модулей
        return {
            ...pageSectionSettings,
            ...sectionSettings,
            status: pageSectionSettings.status ?? sectionSettings.status,
            settings: withModules ? sectionSettings.settings : omit(sectionSettings?.settings, 'modules'),
        };
    }
);

export const [useConstructorPageSettings, getConstructorPageSettings] = constructorSlice.createSelector(
    (state, pageId: string, isPreview?: boolean): PreviewPage | undefined => {
        if (!state.pages[pageId]) {
            return undefined;
        }

        const sectionTemplateFragments = isPreview
            ? buildConstructorPartialDtosFromFragments(state.fragments)
            : undefined;

        return {
            ...state.pages[pageId],
            ...(sectionTemplateFragments ? { section_template_fragments: sectionTemplateFragments } : {}),
            layout: Object.values(state.pages[pageId].layout)
                .sort((a, b) => a.display_sequence - b.display_sequence)
                .reduce((layout: PreviewSection[], section) => {
                    let sectionSettings: PreviewSection = {
                        ...state.sections[section.id],
                        id: section.id,
                        display_sequence: section.display_sequence,
                        section_type: section.section_type,
                        instance_id: section.instance_id,
                        status: section.status ?? state.sections[section.id]?.status,
                    };

                    if (sectionSettings.template_id && isPreview) {
                        sectionSettings = {
                            ...sectionSettings,
                            template: {
                                ...state.templates[sectionSettings.template_id],
                            },
                        };
                    }

                    if (isPreview) {
                        sectionSettings = mapKeysDeep(sectionSettings, settingsKeyMap);
                    }

                    layout.push(sectionSettings);

                    return layout;
                }, []),
        } as PreviewPage;
    }
);

export const [useConstructorPagesSettings, getConstructorPagesSettings] = constructorSlice.createSelector(
    (state, isPreview?: boolean) =>
        Object.keys(state.pages).reduce(
            (
                allPages: {
                    [pageId: string]: Page;
                },
                pageId
            ) => {
                const pageSettings = getConstructorPageSettings(pageId, isPreview);

                if (pageSettings) {
                    allPages[pageId] = pageSettings;
                }

                return allPages;
            },
            {}
        )
);

const constructorSectionCountSelector = constructorSlice.createSelector((state, widget: string) => {
    const totalCount = Object.values(state.sections).filter(
        (section) =>
            section.widget === widget || (section.template_id && state.templates[section.template_id]?.alias === widget)
    ).length;

    if (totalCount === 0 || !state.pages[state.page]) {
        return { totalCount, count: 0 };
    }

    const count = Object.values(state.pages[state.page].layout).filter(
        (section) =>
            state.sections[section.id].widget === widget ||
            (state.sections[section.id]?.template_id &&
                state.templates[state.sections[section.id]?.template_id ?? '']?.alias === widget)
    ).length;

    return { totalCount, count };
});

export const getConstructorSectionCount = constructorSectionCountSelector[1];

export const [useConstructorTemplates, getConstructorTemplates] = constructorSlice.createSelector(
    (state) => state.templates
);

export const [useConstructorFragments, getConstructorFragments] = constructorSlice.createSelector(
    (state) => state.fragments
);

async function getInitialConstructorVersion(version?: number) {
    if (version === undefined) {
        return getLatestVersion({}, { forceRefetch: true });
    }

    try {
        return await getVersion({ id: version });
    } catch (error) {
        showError({ label: 'Не удалось загрузить версию редактора' }, error);

        return getLatestVersion({}, { forceRefetch: true });
    }
}

export const initConstructor = store.createAsyncThunk(
    'initConstructor',
    async ({
        migrateGlobalSettings,
        migrateSectionSettings,
        version,
    }: {
        migrateGlobalSettings?: YandexKit['migrateGlobalSettings'];
        migrateSectionSettings?: YandexKit['migrateSectionSettings'];
        version?: number;
    }) => {
        const startedAtTemplateDraftGeneration = getConstructorPreviewState().templateDraftGeneration;
        const templates = await getConstructorTemplateSections();
        const constructorVersion = await getInitialConstructorVersion(version);
        const isDirty = version !== undefined && constructorVersion.version.id === version;
        const globalSettings = migrateGlobalSettings
            ? migrateGlobalSettings(constructorVersion.global_settings)
            : constructorVersion.global_settings;
        const pages = migrateSectionSettings
            ? constructorVersion.pages.map((page) => {
                  const layout = (page.layout ?? []).map((section) => {
                      const sectionSettings = migrateSectionSettings(section.widget, section.settings);

                      return {
                          ...section,
                          settings: sectionSettings,
                      };
                  });

                  return {
                      ...page,
                      layout,
                  };
              })
            : constructorVersion.pages;

        return {
            ...constructorVersion,
            global_settings: globalSettings,
            pages,
            templates,
            isDirty,
            startedAtTemplateDraftGeneration,
        };
    }
);

constructorSlice.addExtraReducers((builder) => {
    builder.addCase(initConstructor.fulfilled, (state, action) => {
        const { pages, global_settings, version, templates, isDirty, startedAtTemplateDraftGeneration } =
            action.payload;

        if (startedAtTemplateDraftGeneration !== state.templateDraftGeneration) {
            return;
        }

        const shouldPreserveAppliedTemplate = state.hasAppliedTemplateDraft;

        if (pages && !shouldPreserveAppliedTemplate) {
            const sortedPages = [...pages].sort(
                (a, b) => getConstructorPageOrder(a.alias) - getConstructorPageOrder(b.alias)
            );

            sortedPages.forEach((page) => {
                reducerChangePageSettings(state, {
                    payload: {
                        pageId: page.id,
                        pageSettings: page,
                    },
                    type: 'constructor/changePageSettings',
                });
            });

            const pageId = sortedPages.find((page) => page.alias === state.page)?.id ?? sortedPages[0].id;

            // Если страница уже выбрана, нужно сделать вызов ещё раз, чтобы выставился корректный id
            reducerChangePage(state, {
                payload: state.page || pageId,
                type: 'constructor/changePage',
            });

            const sectionInstanceId = sortedPages
                .find((page) => page.id === pageId)
                ?.layout.find((section) => section.widget === state.currentSection?.sectionInstanceId)?.instance_id;

            if (sectionInstanceId) {
                reducerChangeCurrentSection(state, {
                    payload: {
                        sectionInstanceId,
                        moduleId: state.currentSection?.moduleId,
                    },
                    type: 'constructor/changeCurrentSection',
                });
            }
        }

        if (global_settings && !shouldPreserveAppliedTemplate) {
            reducerChangeGlobalSettings(state, {
                payload: global_settings,
                type: 'constructor/changeGlobalSettings',
            });
        }

        if (templates) {
            templates.forEach((template: OpenApiSchemaSectionTemplate) => {
                reducerChangeTemplate(state, {
                    payload: {
                        templateId: template.id,
                        template: template,
                    },
                    type: 'constructor/changeTemplateSettings',
                });
            });
        }

        if (action.payload.section_template_fragments) {
            reducerSetFragments(state, {
                payload: action.payload.section_template_fragments,
                type: 'constructor/setFragments',
            });
        }

        reducerChangeActiveVersion(state, {
            payload: version.id,
            type: 'constructor/changeActiveVersion',
        });

        if (!shouldPreserveAppliedTemplate) {
            reducerChangeIsDirty(state, {
                payload: isDirty,
                type: 'constructor/changeIsDirty',
            });
        }

        if (!shouldPreserveAppliedTemplate && !(pages && global_settings)) {
            reducerChangeIsError(state, {
                payload: true,
                type: 'constructor/changeIsError',
            });
        }
    });
});

export const deleteConstructorPage = (pageId: string) => {
    logEventString(constructorProductLoggingEvents.Page.Delete, 'true', { pageId });

    return constructorSlice.reducerDeletePage(pageId);
};
